import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { anonymousAPI, AnonymousSession } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { getLocalizedPath } from '../i18n';
import {
  RefreshCwIcon,
  AlertCircleIcon,
  CheckIcon,
  FileTextIcon,
  TrendingUpIcon,
  ShieldIcon,
  DollarSignIcon,
  CalendarIcon,
  AlertIcon,
  ClipboardIcon,
  UserIcon,
  CreditCardIcon,
  BuildingIcon,
  PawPrintIcon,
  PaintbrushIcon,
  WrenchIcon,
  SearchIcon,
  HomeIcon,
  MailIcon,
  MenuIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  ZapIcon,
  ScaleIcon,
  LightbulbIcon,
  ArrowRightIcon,
  ChatIcon,
  ClockIcon
} from '../components/Icons';
import { SectionHeader, SummaryCard } from '../components/ui';
import ContractProcessingCard from '../components/ContractProcessingCard';
import { RISK_DISCLAIMER } from '../constants/sections';
import { showBrowserNotification } from '../utils/browserNotification';
import './Landing.css';
import './DocumentDetailNew.css';

const PROCESSING_STAGE_INTERVAL_MS = 8000;

type StrippedTemplate = {
  text: string;
  vars: string[];
  ranges: Array<{ start: number; end: number }>;
};


const getRandomMotivation = (t?: any) => {
  try {
    const list = t ? (t('documents:processing.motivations', { returnObjects: true }) as unknown as string[]) : [];
    if (Array.isArray(list) && list.length > 0) {
      const idx = Math.floor(Math.random() * list.length);
      return list[idx];
    }
  } catch (e) {
    return '';
  }
  return t ? (t('documents:processing.motivations.0', { defaultValue: '' }) as string) : '';
};

const stripTemplateBraces = (suggestion: string): StrippedTemplate => {
  const vars: string[] = [];
  const ranges: Array<{ start: number; end: number }> = [];
  let output = '';
  let lastIndex = 0;
  let outputIndex = 0;

  const matches = Array.from(suggestion.matchAll(/\{([^}]+)\}/g));
  matches.forEach((match) => {
    const matchIndex = match.index ?? 0;
    const before = suggestion.slice(lastIndex, matchIndex);
    output += before;
    outputIndex += before.length;

    const varName = match[1] ?? '';
    vars.push(varName);
    ranges.push({ start: outputIndex, end: outputIndex + varName.length });
    output += varName;
    outputIndex += varName.length;

    lastIndex = matchIndex + match[0].length;
  });

  if (lastIndex < suggestion.length) {
    output += suggestion.slice(lastIndex);
  }

  return { text: output, vars, ranges };
};

const renderSuggestedQuestionLabel = (suggestion: string) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const matches = Array.from(suggestion.matchAll(/\{([^}]+)\}/g));

  matches.forEach((match, i) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      parts.push(<span key={`t-${i}-${lastIndex}`}>{suggestion.slice(lastIndex, matchIndex)}</span>);
    }

    const varName = match[1] ?? '';
    parts.push(
      <span key={`p-${i}-${matchIndex}`} className="qa-template-var">
        {varName}
      </span>
    );

    lastIndex = matchIndex + match[0].length;
  });

  if (lastIndex < suggestion.length) {
    parts.push(<span key={`t-end-${lastIndex}`}>{suggestion.slice(lastIndex)}</span>);
  }

  return parts.length ? parts : suggestion;
};

const shouldShowTemplateHint = (question: string, vars: string[]) => {
  return vars.some((v) => {
    if (!v) return false;
    if (/^[A-Z]$/.test(v)) {
      return new RegExp(`\\b${v}\\b`).test(question);
    }
    return question.includes(v);
  });
};

type AuthModalContent = {
  title: string;
  subtitle: string;
};

const normalizeSummary = (raw: any) => {
  if (!raw) return {};

  let parsed = raw;

  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }

  if (Array.isArray(parsed)) {
    const converted: Record<string, any> = {};
    parsed.forEach((item: any, idx: number) => {
      if (item && typeof item === 'object') {
        const label = item.label || item.nome || `campo_${idx}`;
        const key = label.toString().trim().toLowerCase().replace(/\s+/g, '_');
        converted[key] = item.value ?? item.valor ?? item.text ?? item.descricao;
      }
    });
    parsed = converted;
  }

  const base = parsed?.mandatory_fields || parsed || {};
  const normalized: any = { ...base };
  const mappings: Record<string, string> = {
    rental_value: 'valor_aluguel',
    valor: 'valor_aluguel',
    rent_value: 'valor_aluguel',
    duration: 'prazo_locacao',
    contract_duration: 'prazo_locacao',
    start_date: 'data_inicio',
    end_date: 'data_termino',
    adjustment_index: 'reajuste',
    reajuste: 'reajuste',
    guarantee: 'garantia',
    guarantees: 'garantia',
    penalties: 'multas',
    fines: 'multas',
    penalty: 'multas',
    payment_terms: 'pagamento',
    payment: 'pagamento',
    condominium_fees: 'condominio',
    condo_fee: 'condominio',
    pets: 'animais_permitidos',
    animais: 'animais_permitidos',
    repairs: 'reparos',
    maintenance: 'reparos',
    inspection: 'vistoria',
    uso_imovel: 'uso_imovel',
  };

  Object.entries(mappings).forEach(([source, target]) => {
    if (base[source] && !normalized[target]) {
      if (target === 'reajuste' && typeof base[source] === 'string') {
        normalized[target] = { indice: base[source] };
      } else {
        normalized[target] = base[source];
      }
    }
  });

  // Consolidate prazo_locacao into a structured object with duration and dates
  const prazoValue = normalized.prazo_locacao;
  const dataInicio = normalized.data_inicio;
  const dataTermino = normalized.data_termino;

  // Only create structured prazo_locacao if we have any of these fields
  if (prazoValue || dataInicio || dataTermino) {
    const prazoObj: Record<string, any> = {};

    // If prazo_locacao is already an object, merge it
    if (typeof prazoValue === 'object' && prazoValue !== null) {
      Object.assign(prazoObj, prazoValue);
    } else if (prazoValue) {
      prazoObj.duracao = prazoValue;
    }

    // Add dates if available and not already in the object
    if (dataInicio && !prazoObj.data_inicio) {
      prazoObj.data_inicio = dataInicio;
    }
    if (dataTermino && !prazoObj.data_termino) {
      prazoObj.data_termino = dataTermino;
    }

    // Only set as object if we have multiple fields, otherwise keep simple value
    if (Object.keys(prazoObj).length > 1) {
      normalized.prazo_locacao = prazoObj;
    } else if (Object.keys(prazoObj).length === 1) {
      normalized.prazo_locacao = Object.values(prazoObj)[0];
    }
  }

  return normalized;
};

type FloatingNavItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

type FloatingSectionNavProps = {
  items: FloatingNavItem[];
  sectionsLabel: string;
  hintText: string;
};

const FloatingSectionNav: React.FC<FloatingSectionNavProps> = ({ items, sectionsLabel, hintText }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [activeId, setActiveId] = React.useState<string | null>(items[0]?.id ?? null);
  const [showHint, setShowHint] = React.useState(false);
  const hintSeenRef = React.useRef(false);
  const isHoveringRef = React.useRef(false);
  const pinnedRef = React.useRef(false);

  const markHintSeen = useCallback(() => {
    if (hintSeenRef.current) return;
    hintSeenRef.current = true;
    setShowHint(false);
    try {
      window.localStorage.setItem('docDetailFloatingNavHintSeen', '1');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem('docDetailFloatingNavHintSeen') === '1';
      hintSeenRef.current = seen;
      if (!seen) setShowHint(true);
    } catch {
      setShowHint(true);
    }
  }, []);

  useEffect(() => {
    if (!showHint) return;
    const timer = window.setTimeout(() => {
      markHintSeen();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [showHint, markHintSeen]);

  useEffect(() => {
    if (items.length === 0) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) => (prev && items.some((i) => i.id === prev) ? prev : items[0].id));
  }, [items]);

  useEffect(() => {
    if (items.length === 0) return;

    const elements = items
      .map((item) => window.document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best?.target?.id) setActiveId(best.target.id);
      },
      { root: null, rootMargin: '-20% 0px -65% 0px', threshold: [0.15, 0.25, 0.4] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (pinnedRef.current) return;
      if (isHoveringRef.current) return;
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;

      const paddingX = 64;
      const paddingY = 180;
      const within =
        event.clientX >= rect.left - paddingX &&
        event.clientX <= rect.right + paddingX &&
        event.clientY >= rect.top - paddingY &&
        event.clientY <= rect.bottom + paddingY;

      if (within) {
        setOpen(true);
        markHintSeen();
      } else if (open) {
        setOpen(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [markHintSeen, open]);

  if (items.length < 2) return null;

  const activeItem = items.find((item) => item.id === activeId) || items[0];

  const wheelSize = 276;
  const centerX = 30;
  const centerY = wheelSize / 2;
  const radius = 122;
  const angleStart = -70;
  const angleEnd = 70;
  const step = items.length === 1 ? 0 : (angleEnd - angleStart) / (items.length - 1);

  return (
    <div
      ref={rootRef}
      className={`floating-nav ${open ? 'is-open' : ''} ${showHint ? 'has-hint' : ''}`}
      onMouseEnter={() => {
        isHoveringRef.current = true;
        setOpen(true);
        markHintSeen();
      }}
      onMouseLeave={() => {
        isHoveringRef.current = false;
        if (!pinnedRef.current) setOpen(false);
      }}
    >
      <button
        type="button"
        className="floating-nav-handle"
        aria-label={sectionsLabel}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          markHintSeen();
          setOpen((prev) => {
            const next = !prev;
            pinnedRef.current = next;
            return next;
          });
        }}
        onFocus={() => {
          markHintSeen();
          setOpen(true);
        }}
        onBlur={() => {
          if (!pinnedRef.current) setOpen(false);
        }}
      >
        <span className="floating-nav-handle-icon" aria-hidden="true">
          {activeItem?.icon}
        </span>
        <span className="floating-nav-handle-text">{activeItem?.label || sectionsLabel}</span>
      </button>

      {showHint && (
        <div className="floating-nav-hint" role="note">
          {hintText}
        </div>
      )}

      <div
        className="floating-nav-wheel"
        style={{ width: wheelSize, height: wheelSize }}
        role="navigation"
        aria-label={sectionsLabel}
      >
        {items.map((item, index) => {
          const deg = angleStart + index * step;
          const rad = (deg * Math.PI) / 180;
          const x = centerX + Math.cos(rad) * radius;
          const y = centerY + Math.sin(rad) * radius;

          return (
            <button
              key={item.id}
              type="button"
              className={`floating-nav-item ${activeId === item.id ? 'is-active' : ''}`}
              style={{ left: x, top: y }}
              data-label={item.label}
              aria-label={item.label}
              onClick={() => {
                window.document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {item.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AnonymousAnalysis: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, migrateAnonymousSession } = useAuth();
  const { t, i18n: i18nInstance } = useTranslation(['documents', 'common', 'landing']);
  const [session, setSession] = useState<AnonymousSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedMotivation] = useState<string>(() => {
    const candidate = getRandomMotivation(t);
    if (candidate && candidate.trim().length > 0) return candidate;
    return t('documents:processing.motivations.0', { defaultValue: '' }) as string;
  });
  const getAuthContent = useCallback((kind: 'save' | 'qa' | 'notif'): AuthModalContent => {
    return {
      title: t(`documents:preview.auth.${kind}.title`),
      subtitle: t(`documents:preview.auth.${kind}.subtitle`),
    };
  }, [t]);
  const [authModalContent, setAuthModalContent] = useState<AuthModalContent>(() => getAuthContent('save'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [question, setQuestion] = useState('');
  const [isPreviewAsking, setIsPreviewAsking] = useState(false);
  const [activeTemplateVars, setActiveTemplateVars] = useState<string[]>([]);
  const [selectedRiskFilter, setSelectedRiskFilter] = useState<'high' | 'medium' | 'low' | null>(null);
  const [currentPreviewRisk, setCurrentPreviewRisk] = useState(0);
  const questionInputRef = useRef<HTMLTextAreaElement>(null);
  const prevStatusRef = useRef<string | null>(null);
  const processingStagesAnon = useMemo(() => [
    { title: t('documents:detail.processing.steps.preparing.title'), subtitle: t('documents:detail.processing.steps.preparing.subtitle'), icon: <RefreshCwIcon size={18} className="spin" /> },
    { title: t('documents:detail.processing.steps.extracting.title'), subtitle: t('documents:detail.processing.steps.extracting.subtitle'), icon: <FileTextIcon size={18} /> },
    { title: t('documents:detail.processing.steps.identifying.title'), subtitle: t('documents:detail.processing.steps.identifying.subtitle'), icon: <SearchIcon size={18} /> },
    { title: t('documents:detail.processing.steps.context.title'), subtitle: t('documents:detail.processing.steps.context.subtitle'), icon: <ScaleIcon size={18} /> },
    { title: t('documents:detail.processing.steps.risks.title'), subtitle: t('documents:detail.processing.steps.risks.subtitle'), icon: <ShieldIcon size={18} /> },
    { title: t('documents:detail.processing.steps.recommendations.title'), subtitle: t('documents:detail.processing.steps.recommendations.subtitle'), icon: <LightbulbIcon size={18} /> },
    { title: t('documents:detail.processing.steps.report.title'), subtitle: t('documents:detail.processing.steps.report.subtitle'), icon: <ZapIcon size={18} /> },
    { title: t('documents:detail.processing.steps.finalizing.title'), subtitle: t('documents:detail.processing.steps.finalizing.subtitle'), icon: <CheckIcon size={18} /> },
  ], [t]);
  const userRole = session?.user_role || 'tenant';
  const perspectiveLabel = userRole === 'landlord' ? t('common:roles.landlord') : t('common:roles.tenant');
  const suggestedQuestionsRaw = t(`documents:detail.questions.suggestedQuestions.${userRole}`, { returnObjects: true });
  const suggestedQuestions = Array.isArray(suggestedQuestionsRaw) ? (suggestedQuestionsRaw as string[]) : [];
  const riskDisclaimer = t('documents:detail.risks.disclaimer', { defaultValue: RISK_DISCLAIMER });

  const resizeQuestionInput = useCallback(() => {
    const input = questionInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => {
    resizeQuestionInput();
  }, [question, resizeQuestionInput]);

  useEffect(() => {
    setCurrentPreviewRisk(0);
  }, [selectedRiskFilter, sessionId]);

  // Generate fake risks dynamically based on actual risk counts from backend
  const generateFakeRisks = (high: number, medium: number, low: number) => {
    const isEnglish = i18nInstance.language?.startsWith('en');
    const fakeRiskTemplates = {
      high: [
        {
          title: isEnglish ? 'Early termination penalty above the legal limit' : 'Multa por rescisão antecipada acima do limite legal',
          potential_impact: isEnglish
            ? 'The contract sets an early termination penalty that may exceed the legal proportional limit. Depending on when termination happens, you may have to pay more than expected.'
            : 'O contrato prevê uma multa por rescisão antecipada que pode ultrapassar o limite legal de 3 aluguéis proporcionais. Dependendo do momento da rescisão, você pode ser obrigado a pagar um valor significativamente maior do que o previsto em lei, comprometendo seu orçamento.',
          clause_text: isEnglish
            ? 'If the tenant terminates early, the tenant must pay a penalty equivalent to three current monthly rents, without proportional reduction for the period already fulfilled.'
            : 'Em caso de rescisão antecipada pelo LOCATÁRIO, este ficará obrigado ao pagamento da multa contratual equivalente a 3 (três) aluguéis vigentes na data da rescisão, sem proporcionalidade ao período cumprido, além dos encargos locatícios em aberto.',
          explanation: isEnglish
            ? 'Brazilian tenancy law generally requires early termination penalties to be proportional to the remaining contract period. A fixed penalty can be challenged and should be negotiated before signing.'
            : 'A Lei 8.245/91 (Lei do Inquilinato) prevê que a multa por rescisão antecipada deve ser proporcional ao período restante do contrato. Cláusulas que estabelecem multa fixa, sem proporcionalidade, podem ser consideradas abusivas e contestadas judicialmente. Crie uma conta para ver a análise completa e o que você pode negociar.',
          recommendation: { suggestion: isEnglish ? 'Ask for explicit proportionality in the penalty clause and confirm the calculation method before signing.' : 'Negocie a inclusão de proporcionalidade explícita na cláusula de multa, conforme Art. 4º da Lei 8.245/91. Para ver a sugestão exata de texto e como negociar, crie uma conta gratuita.' }
        },
        {
          title: isEnglish ? 'Rent adjustment without an official index' : 'Reajuste sem índice oficial definido',
          potential_impact: isEnglish
            ? 'The rent can be adjusted using an index chosen by the landlord, without being limited to an official benchmark. This can create unpredictable annual increases.'
            : 'O contrato permite reajuste do aluguel por índice a critério do locador, sem limitar a um indexador oficial. Isso pode resultar em aumentos anuais muito acima da inflação real, tornando o aluguel insustentável ao longo do contrato.',
          clause_text: isEnglish
            ? 'The rent may be adjusted annually by an inflation index to be defined by the landlord or by any other index that reflects cost-of-living changes.'
            : 'O valor do aluguel poderá ser reajustado anualmente pelo índice de inflação a ser definido pelo LOCADOR, ou por qualquer outro índice que reflita a variação do custo de vida, a critério exclusivo do LOCADOR.',
          explanation: isEnglish
            ? 'Without a fixed index such as IPCA or IGP-M, the landlord may choose the least favorable index each year. The market standard is to agree on an official index in advance.'
            : 'Sem fixar o índice de reajuste (IPCA, IGP-M ou outro), o locador tem liberdade para escolher o índice mais desfavorável para você em cada ano. O padrão de mercado é vincular o reajuste a um índice oficial acordado previamente entre as partes. Crie uma conta para ver a análise jurídica completa.',
          recommendation: { suggestion: isEnglish ? 'Request a specific official index and a clear annual adjustment cap.' : 'Exija a definição de um índice oficial específico (preferencialmente IPCA) e teto máximo de reajuste anual. Crie uma conta gratuita para ver a sugestão de texto pronta para usar.' }
        }
      ],
      medium: [
        {
          title: isEnglish ? 'Responsibility for extraordinary condo fees' : 'Responsabilidade por taxas condominiais extraordinárias',
          potential_impact: isEnglish ? 'You may become responsible for extraordinary condo charges, such as building works or unexpected renovations, which are usually the owner’s responsibility.' : 'Você pode ser responsabilizado por taxas condominiais extraordinárias, como obras de manutenção do prédio ou reformas não previstas no condomínio, que geralmente são obrigação do proprietário.',
          clause_text: isEnglish ? 'The tenant will be responsible for ordinary and extraordinary condo fees and any charges approved during the lease term.' : 'O LOCATÁRIO ficará responsável pelo pagamento das taxas ordinárias e extraordinárias do condomínio, bem como por quaisquer encargos que venham a ser deliberados em assembleia condominial durante o período da locação.',
          explanation: isEnglish ? 'Extraordinary condo expenses are generally the landlord’s obligation under Brazilian tenancy law. This clause shifts costs that may be significant and unpredictable.' : 'Pela Lei do Inquilinato (Art. 22, X), despesas extraordinárias de condomínio são obrigação do locador, não do locatário. Esta cláusula transfere para você uma responsabilidade que legalmente não é sua, podendo representar custos significativos e imprevisíveis.',
          recommendation: { suggestion: isEnglish ? 'Limit tenant responsibility to ordinary condo fees only.' : 'Solicite a limitação da sua responsabilidade às taxas ordinárias do condomínio, conforme previsto em lei. Crie uma conta para ver as recomendações detalhadas.' }
        },
        {
          title: isEnglish ? 'No deadline to return the security deposit' : 'Prazo de devolução do depósito caução não especificado',
          potential_impact: isEnglish ? 'The contract does not clearly define when the security deposit must be returned, exposing you to delays after move-out.' : 'O contrato não define claramente o prazo para devolução do depósito caução ao final da locação, deixando você sujeito a atrasos na restituição do valor pago.',
          clause_text: isEnglish ? 'The security deposit will be returned after move-out inspection and verification of the property condition, after deducting amounts owed, with no defined return deadline.' : 'O depósito caução será devolvido ao LOCATÁRIO após a vistoria de saída e verificação do estado do imóvel, descontados os valores eventualmente devidos, sem prazo determinado para a restituição.',
          explanation: isEnglish ? 'Without a clear deadline, deposit return can be delayed. A common market expectation is return within 30 days after key handover.' : 'A falta de prazo definido para devolução do depósito pode resultar em atrasos injustificados. O padrão de mercado é devolver o depósito em até 30 dias após a entrega das chaves. Sem esse prazo contratual, fica mais difícil exigir a restituição tempestiva.',
          recommendation: { suggestion: isEnglish ? 'Add a maximum deadline for deposit return after move-out inspection.' : 'Inclua no contrato prazo máximo de 30 dias para devolução do depósito após a vistoria de saída. Crie uma conta para ver o texto exato sugerido.' }
        },
        {
          title: isEnglish ? 'Mandatory repainting at the end of the lease' : 'Obrigação de pintura ao final do contrato',
          potential_impact: isEnglish ? 'The contract requires repainting when leaving, regardless of the initial condition. This can create an unexpected move-out cost.' : 'O contrato exige que você entregue o imóvel pintado ao final da locação, independentemente do estado em que estava ao entrar. Isso representa um custo adicional não previsto no momento da assinatura.',
          clause_text: isEnglish ? 'The tenant must return the property with new internal and external paint in the original colors, otherwise the cost may be deducted from the deposit.' : 'O LOCATÁRIO obriga-se a devolver o imóvel ao término da locação com pintura nova, interna e externamente, conforme cores originais, sob pena de desconto do valor correspondente do depósito caução.',
          explanation: isEnglish ? 'Paint is often treated as normal wear unless the property was delivered freshly painted and this was documented. The obligation should be tied to the entry inspection.' : 'Pintura é considerada desgaste natural do imóvel e, em regra, não deve ser exigida do locatário ao final da locação, a menos que o imóvel tenha sido entregue recém-pintado e com laudo comprovando isso. Esta cláusula pode ser considerada abusiva dependendo do contexto.',
          recommendation: { suggestion: isEnglish ? 'Ask to tie repainting obligations to documented damage or the entry inspection report.' : 'Questione a obrigatoriedade de pintura e registre o estado atual das paredes na vistoria de entrada com fotos detalhadas. Crie uma conta para ver as recomendações completas.' }
        }
      ],
      low: [
        {
          title: isEnglish ? 'Small repairs without a value limit' : 'Responsabilidade por pequenos reparos sem limite de valor',
          potential_impact: isEnglish ? 'You are responsible for everyday repairs, but the contract does not define the value limit for a small repair, which can cause disputes.' : 'Você é responsável por reparos de uso cotidiano, mas o contrato não define o limite de valor que caracteriza "pequeno reparo", podendo gerar disputas sobre quem deve arcar com determinadas manutenções.',
          clause_text: isEnglish ? 'Repairs resulting from normal daily use are the tenant’s exclusive responsibility, including faucets, locks, glass, switches, and other frequent-use components.' : 'Os reparos decorrentes do uso normal e cotidiano do imóvel são de responsabilidade exclusiva do LOCATÁRIO, incluindo consertos de torneiras, fechaduras, vidros, interruptores e demais componentes de uso frequente.',
          explanation: isEnglish ? 'A value cap avoids ambiguity. Without it, higher-cost maintenance may become disputed.' : 'Embora a lei preveja que pequenos reparos são responsabilidade do locatário, a ausência de um limite de valor pode gerar ambiguidade. Sem essa definição, reparos de valor relevante podem ser disputados, com cada parte alegando que é responsabilidade da outra.',
          recommendation: { suggestion: isEnglish ? 'Define a maximum amount per repair above which the landlord is responsible.' : 'Defina no contrato um valor máximo por reparo (por ex., R$ 300,00) acima do qual a responsabilidade recai sobre o locador. Crie uma conta para ver o texto exato.' }
        },
        {
          title: isEnglish ? 'Forum clause without tenant choice' : 'Cláusula de foro sem opção ao locatário',
          potential_impact: isEnglish ? 'The contract chooses the court venue without considering the tenant’s domicile, which can make disputes harder and more expensive.' : 'O contrato fixa o foro para resolução de disputas sem considerar o domicílio do locatário, o que pode dificultar e encarecer o acesso à justiça em caso de conflito.',
          clause_text: isEnglish ? 'The court venue of [city] is elected, excluding any other, to resolve disputes arising from this contract.' : 'Fica eleito o Foro da Comarca de [cidade], com exclusão de qualquer outro, por mais privilegiado que seja, para dirimir quaisquer questões oriundas do presente contrato.',
          explanation: isEnglish ? 'A unilateral venue clause may be inconvenient or challenged depending on the case. Confirm that the venue matches the property location or your domicile.' : 'A fixação unilateral do foro, especialmente se diferente do domicílio do locatário, pode ser considerada abusiva pelo CDC. Embora seja comum em contratos, vale atenção caso o foro definido seja diferente da cidade onde o imóvel está localizado.',
          recommendation: { suggestion: isEnglish ? 'Check whether the selected venue is practical and request an adjustment if needed.' : 'Verifique se o foro definido corresponde à localização do imóvel e ao seu domicílio. Se não, solicite ajuste. Crie uma conta para orientações completas.' }
        }
      ]
    };

    const risks: any[] = [];

    // Add high severity risks
    for (let i = 0; i < high; i++) {
      const template = fakeRiskTemplates.high[i % fakeRiskTemplates.high.length];
      risks.push({ severity: 'high', ...template });
    }

    // Add medium severity risks
    for (let i = 0; i < medium; i++) {
      const template = fakeRiskTemplates.medium[i % fakeRiskTemplates.medium.length];
      risks.push({ severity: 'medium', ...template });
    }

    // Add low severity risks
    for (let i = 0; i < low; i++) {
      const template = fakeRiskTemplates.low[i % fakeRiskTemplates.low.length];
      risks.push({ severity: 'low', ...template });
    }

    return risks;
  };

  const fetchResults = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await anonymousAPI.getResults(sessionId);
      const data = response.data;
      setSession(data);

      // If still processing, poll for updates
      if (data.status === 'processing') {
        setTimeout(fetchResults, 3000); // Poll every 3 seconds
      }

      setSessionLoading(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(t('common:errors.notFound'));
      } else if (err.response?.status === 410) {
        const errorDetail = err.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string'
          ? errorDetail
          : errorDetail?.message || t('common:errors.generic');
        setError(errorMessage);
      } else {
        setError(t('common:errors.generic'));
      }
      setSessionLoading(false);
    }
  }, [sessionId, t]);

  useEffect(() => {
    if (!sessionId) {
      setError(t('common:errors.notFound'));
      setSessionLoading(false);
      return;
    }

    // Store session_id in sessionStorage for migration after signup/login
    sessionStorage.setItem('anonymous_session_id', sessionId);

    fetchResults();
  }, [sessionId, fetchResults, t]);

  // Scroll to top when navigating to the preview page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [sessionId]);

  useEffect(() => {
    if (session?.status !== 'processing') {
      setProcessingStage(0);
      return;
    }
    setProcessingStage(0);
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      setProcessingStage(() => {
        const next = Math.min(current, processingStagesAnon.length - 1);
        return next;
      });
      if (current >= processingStagesAnon.length - 1) {
        clearInterval(interval);
      }
    }, PROCESSING_STAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [session?.status, sessionId, processingStagesAnon.length]);

  // Show browser notification when analysis completes
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const currentStatus = session?.status;

    // Trigger notification when status changes from 'processing' to 'completed'
    if (prevStatus === 'processing' && currentStatus === 'completed') {
      showBrowserNotification(
        t('documents:preview.analysisComplete.title', { defaultValue: 'Analysis Complete' }),
        {
          body: t('documents:preview.analysisComplete.body', { defaultValue: 'Your contract analysis is ready to view.' }),
          icon: '/logo_icon_only.png',
          tag: `analysis-complete-${sessionId}`,
        }
      );
    }

    prevStatusRef.current = currentStatus ?? null;
  }, [session?.status, sessionId, t]);

  const openAuthModal = (kind: 'save' | 'qa' | 'notif') => {
    setAuthModalContent(getAuthContent(kind));
    setShowAuthModal(true);
  };

  const handleSaveClick = async () => {
    if (user) {
      await migrateAnonymousSession();
      navigate(getLocalizedPath('/catalog', i18nInstance.language));
      return;
    }
    openAuthModal('save');
  };

  const handlePreviewQuestion = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsPreviewAsking(true);
    try {
      if (user) {
        await migrateAnonymousSession();
        navigate(getLocalizedPath('/catalog', i18nInstance.language));
        return;
      }
      openAuthModal('qa');
    } finally {
      setIsPreviewAsking(false);
    }
  };

  const handleGoToAuth = (path: '/login' | '/signup') => {
    navigate(getLocalizedPath(path, i18nInstance.language), { state: { from: location.pathname } });
  };

  const getUserInitials = (name?: string | null) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase() || '').join('') || 'U';
  };

  const renderNav = () => (
    <nav className="landing-nav">
      <div className="landing-container">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate(getLocalizedPath('/', i18nInstance.language))} style={{ cursor: 'pointer' }}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="nav-links desktop-only"></div>
          <div className="nav-ctas desktop-only">
            {!authLoading && user ? (
              <button onClick={() => navigate(getLocalizedPath('/catalog', i18nInstance.language))} className="account-cta">
                <span className="account-cta-avatar">
                  {getUserInitials(user.full_name || user.email)}
                </span>
                <span className="account-cta-text">
                  <strong>{t('common:nav.myAccount')}</strong>
                  <small>{user.full_name || user.email}</small>
                </span>
              </button>
            ) : (
              <>
                <button onClick={() => navigate(getLocalizedPath('/login', i18nInstance.language))} className="btn-secondary-landing">
                  {t('common:nav.login')}
                </button>
                <button onClick={() => navigate(getLocalizedPath('/signup', i18nInstance.language))} className="btn-primary-landing">
                  {t('common:nav.signup')}
                </button>
              </>
            )}
          </div>
          <button
            className="mobile-menu-btn mobile-only"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu">
            {!authLoading && user ? (
              <button onClick={() => navigate(getLocalizedPath('/catalog', i18nInstance.language))} className="btn-primary-landing">
                {t('common:nav.myAccount')}
              </button>
            ) : (
              <>
                <button onClick={() => navigate(getLocalizedPath('/login', i18nInstance.language))} className="btn-secondary-landing">
                  {t('common:nav.login')}
                </button>
                <button onClick={() => navigate(getLocalizedPath('/signup', i18nInstance.language))} className="btn-primary-landing">
                  {t('common:nav.signup')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );

  if (sessionLoading && !session) {
    return (
      <>
        {renderNav()}
        <div className="document-detail-new preview-mode">
          <div className="loading-wrapper">
            <div className="loading-card">
              <h2>{t('documents:preview.loadingTitle')}</h2>
              <p>{t('documents:preview.loadingSubtitle')}</p>
              <div className="loading-progress">
                <div className="loading-progress-bar" />
              </div>
              <p className="loading-motivation">{selectedMotivation}</p>
              <div className="loading-meta">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          </div>
          <footer className="document-footer">
            <div className="document-footer-content">
              <div className="footer-left">
                <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo" />
                <div className="footer-text">
                  <span className="footer-copyright">
                    {t('common:footer.copyright', { year: new Date().getFullYear() })}
                  </span>
                  <span className="footer-tagline">
                    {t('common:footer.tagline', { defaultValue: t('documents:preview.footerTagline') })}
                  </span>
                </div>
              </div>
              <div className="footer-right">
                <span className="footer-tech">{t('common:footer.poweredBy')}</span>
              </div>
            </div>
          </footer>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderNav()}
        <div className="document-detail-new preview-mode">
          <div className="flex items-center justify-center">
            <div className="max-w-md mx-4 bg-white rounded-lg shadow-lg p-8">
              <AlertCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
                {t('documents:preview.errorTitle')}
              </h2>
              <p className="text-gray-600 text-center mb-6">{error}</p>
              <button
                onClick={() => navigate(getLocalizedPath('/', i18nInstance.language))}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('documents:preview.backHome')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!session) {
    return null;
  }

  // Define variables for progressive rendering
  const partial = session.partial_analysis;
  const normalizedSummary = normalizeSummary(partial?.summary);
  const isProcessing = session.status === 'processing';

  // Failed analysis - show error screen
  if (session.status === 'failed' || partial?.error) {
    return (
      <>
        {renderNav()}
      <div className="document-detail-new preview-mode">
          <div className="error-container">
            <div className="error-icon">
              <AlertCircleIcon size={64} />
            </div>
            <h2>{t('documents:preview.errorUnsupportedTitle')}</h2>
            <p className="text-muted">
              {partial?.message || t('documents:preview.errorUnsupportedSubtitle')}
            </p>
            <button
              onClick={() => navigate(getLocalizedPath('/', i18nInstance.language))}
              className="btn-primary"
            >
              {t('documents:preview.newUpload')}
            </button>
          </div>
        </div>
      </>
    );
  }

  const riskCounts = {
    high: partial?.risk_overview?.high || 0,
    medium: partial?.risk_overview?.medium || 0,
    low: partial?.risk_overview?.low || 0,
  };
  const totalRisks = riskCounts.high + riskCounts.medium + riskCounts.low;

  // Generate fake risks based on actual risk counts; replace first with real preview if available
  const fakeDetailedRisks = (() => {
    const fakes = generateFakeRisks(riskCounts.high, riskCounts.medium, riskCounts.low);
    const previewRisk = partial?.preview_risk;
    if (previewRisk && fakes.length > 0) {
      return [{ ...previewRisk }, ...fakes.slice(1)];
    }
    return fakes;
  })();

  const partiesData = partial?.parties?.parties || partial?.parties || {};
  const partyCards = buildPartyCards(partiesData);
  const shouldShowTemplateHelp = activeTemplateVars.length > 0 && shouldShowTemplateHint(question, activeTemplateVars);
  const previewRiskItems = fakeDetailedRisks.map((risk, originalIndex) => ({ risk, originalIndex }));
  const filteredPreviewRisks = selectedRiskFilter
    ? previewRiskItems.filter((item) => item.risk.severity === selectedRiskFilter)
    : previewRiskItems;
  const activePreviewRiskPosition = Math.min(currentPreviewRisk, Math.max(filteredPreviewRisks.length - 1, 0));
  const activePreviewRisk = filteredPreviewRisks[activePreviewRiskPosition];
  const scrollToRiskSectionTop = () => {
    window.requestAnimationFrame(() => {
      window.document
        .getElementById('section-risks-overview')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const showPreviousPreviewRisk = () => {
    if (filteredPreviewRisks.length <= 1) return;
    setCurrentPreviewRisk((current) => (current - 1 + filteredPreviewRisks.length) % filteredPreviewRisks.length);
    scrollToRiskSectionTop();
  };
  const showNextPreviewRisk = () => {
    if (filteredPreviewRisks.length <= 1) return;
    setCurrentPreviewRisk((current) => (current + 1) % filteredPreviewRisks.length);
    scrollToRiskSectionTop();
  };
  const floatingNavItems: FloatingNavItem[] = [
    totalRisks > 0
      ? { id: 'section-risks-overview', label: t('documents:detail.tabs.risks'), icon: <AlertIcon size={18} /> }
      : null,
    Object.keys(normalizedSummary).length > 0 || partyCards.length > 0
      ? { id: 'section-summary', label: t('documents:detail.tabs.summary'), icon: <ClipboardIcon size={18} /> }
      : null,
    !isProcessing
      ? { id: 'section-qa', label: t('documents:detail.tabs.questions'), icon: <ChatIcon size={18} /> }
      : null,
  ].filter(Boolean) as FloatingNavItem[];

  // Show header and progressive content once we have session data
  return (
    <>
      {renderNav()}
      <div className="document-detail-new preview-mode">
        <FloatingSectionNav
          items={floatingNavItems}
          sectionsLabel={t('documents:detail.floatingNav.sections')}
          hintText={t('documents:detail.floatingNav.hoverHint')}
        />
        {/* Header - always shown when we have session */}
        <div className="detail-header-new">
          <div className="header-content">
            <div className="header-utility-bar">
              <button onClick={() => navigate(getLocalizedPath('/', i18nInstance.language))} className="btn-nav">
                ← {t('common:buttons.back')}
              </button>
            </div>

            <div className="header-main-row">
              <div className="header-left">
                <div className="document-title-row">
                  <h1 className="document-title-new">
                    {partial?.contract_type || t('documents:preview.contractTitleFallback')}
                  </h1>
                  <span className="preview-badge">
                    <FileTextIcon size={14} /> {t('documents:preview.badge', { defaultValue: 'Preview' })}
                  </span>
                </div>
                <div className="document-meta-new">
                  <span className="meta-item-new">
                    <CalendarIcon size={16} />
                    {new Date().toLocaleDateString(i18nInstance.language === 'en' ? 'en-US' : 'pt-BR')}
                  </span>
                  {isProcessing ? (
                    <span className="status-badge status-processing">
                      <RefreshCwIcon size={14} className="spin" /> {t('common:status.processing')}
                    </span>
                  ) : (
                    <span className="status-badge status-completed">
                      <CheckIcon size={14} /> {t('documents:preview.statusCompleted')}
                    </span>
                  )}
                  <span className={`perspective-badge perspective-${userRole}`}>
                    <UserIcon size={14} />
                    {perspectiveLabel}
                  </span>
                </div>
              </div>
              {/* Action Group - Right Side */}
              <div className="header-actions-right">
                {/* Notifications CTA */}
                <button
                  type="button"
                  className="notifications-cta"
                  onClick={() => openAuthModal('notif')}
                  disabled={isProcessing}
                >
                  <ClockIcon size={18} />
                  <span>{t('documents:detail.notifications.cta')}</span>
                  <ChevronRightIcon size={16} />
                </button>

                {/* Primary Action - Save */}
                <button onClick={handleSaveClick} className="btn-primary-new">
                  <CheckIcon size={18} /> {t('documents:preview.saveButton')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="detail-content-new">

          {/* Risk Overview Section - First */}
          {totalRisks > 0 && (
            <section id="section-risks-overview" className="detail-section section-tone-1">
              <SectionHeader
                icon={<AlertIcon size={24} />}
                title={t('documents:detail.risks.title')}
                subtitle={t('documents:detail.risks.subtitle')}
              />

              {(() => {
                const totalBrl = typeof partial?.financial_impact_total_brl === 'number' && partial.financial_impact_total_brl > 0
                  ? partial.financial_impact_total_brl
                  : fakeDetailedRisks
                    .filter((risk) => typeof risk.financial_impact_brl === 'number' && risk.financial_impact_brl > 0)
                    .reduce((sum, risk) => sum + risk.financial_impact_brl, 0);
                const formatted = totalBrl > 0
                  ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalBrl)
                  : null;

                return (
                  <>
                    <div className="risk-one-liner">
                      <span className="risk-one-liner-count">
                        {t('documents:detail.risks.oneliner.points', { count: totalRisks })}
                      </span>
                      {riskCounts.high > 0 && (
                        <>
                          <span className="risk-one-liner-sep">·</span>
                          <span className="risk-one-liner-critical">
                            {t('documents:detail.risks.oneliner.critical', { count: riskCounts.high })}
                          </span>
                        </>
                      )}
                      {formatted && (
                        <>
                          <span className="risk-one-liner-sep">·</span>
                          <span className="risk-one-liner-impact">
                            {t('documents:detail.risks.oneliner.estimatedImpact', { amount: formatted })}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="risk-filter-chips">
                      <button
                        type="button"
                        className={`risk-filter-chip${!selectedRiskFilter ? ' active' : ''}`}
                        onClick={() => setSelectedRiskFilter(null)}
                      >
                        {t('documents:detail.risks.filterChips.all', { count: totalRisks })}
                      </button>
                      {riskCounts.high > 0 && (
                        <button
                          type="button"
                          className={`risk-filter-chip chip-high${selectedRiskFilter === 'high' ? ' active' : ''}`}
                          onClick={() => setSelectedRiskFilter((prev) => (prev === 'high' ? null : 'high'))}
                        >
                          {t('documents:detail.risks.filterChips.critical', { count: riskCounts.high })}
                        </button>
                      )}
                      {riskCounts.medium > 0 && (
                        <button
                          type="button"
                          className={`risk-filter-chip chip-medium${selectedRiskFilter === 'medium' ? ' active' : ''}`}
                          onClick={() => setSelectedRiskFilter((prev) => (prev === 'medium' ? null : 'medium'))}
                        >
                          {t('documents:detail.risks.filterChips.moderate', { count: riskCounts.medium })}
                        </button>
                      )}
                      {riskCounts.low > 0 && (
                        <button
                          type="button"
                          className={`risk-filter-chip chip-low${selectedRiskFilter === 'low' ? ' active' : ''}`}
                          onClick={() => setSelectedRiskFilter((prev) => (prev === 'low' ? null : 'low'))}
                        >
                          {t('documents:detail.risks.filterChips.low', { count: riskCounts.low })}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

              {activePreviewRisk && (
                <div className="risk-sections">
                  <div className="risk-slider">
                    <div className="risk-slider-card">
                      <RiskPreviewCard
                        key={`${selectedRiskFilter ?? 'all'}-${activePreviewRisk.originalIndex}-${activePreviewRiskPosition}`}
                        index={activePreviewRisk.originalIndex}
                        locked={activePreviewRisk.originalIndex > 0}
                        risk={activePreviewRisk.risk}
                        onUnlock={handleSaveClick}
                      />
                    </div>
                    {filteredPreviewRisks.length > 1 && (
                      <nav className="slider-nav-pill" aria-label={t('documents:detail.risks.slider.navAria')}>
                        <button className="slider-nav-btn" type="button" onClick={showPreviousPreviewRisk} aria-label={t('documents:detail.risks.slider.prevAria')}>
                          {t('documents:detail.risks.slider.prev')}
                        </button>
                        <span className="slider-nav-count" aria-live="polite" aria-atomic="true">
                          {activePreviewRiskPosition + 1} / {filteredPreviewRisks.length}
                        </span>
                        <button className="slider-nav-btn" type="button" onClick={showNextPreviewRisk} aria-label={t('documents:detail.risks.slider.nextAria')}>
                          {t('documents:detail.risks.slider.next')}
                        </button>
                      </nav>
                    )}
                  </div>
                  <div className="risk-disclaimer">
                    <span>{riskDisclaimer}</span>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Summary Section */}
          {(Object.keys(normalizedSummary).length > 0 || partyCards.length > 0) && (
            <section id="section-summary" className="detail-section section-tone-2">
              {Object.keys(normalizedSummary).length > 0 && (
                <>
                  <SectionHeader
                    icon={<ClipboardIcon size={24} />}
                    title={t('documents:detail.summary.title')}
                    subtitle={t('documents:detail.summary.subtitle')}
                  />

                  <div className="summary-grid-new summary-tier-1">
                    {renderSummaryCards(normalizedSummary, 'tier1')}
                  </div>

                  {renderSummaryCards(normalizedSummary, 'tier2').length > 0 && (
                    <button
                      onClick={() => setSummaryExpanded(!summaryExpanded)}
                      className="summary-expand-btn"
                      aria-expanded={summaryExpanded}
                      aria-controls="summary-tier-2"
                    >
                      <span>{summaryExpanded ? t('documents:preview.summaryShowLess') : t('documents:preview.summaryShowMore')}</span>
                      {summaryExpanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                    </button>
                  )}

                  {summaryExpanded && (
                    <div
                      id="summary-tier-2"
                      className="summary-grid-new summary-tier-2"
                      role="region"
                      aria-label={t('documents:detail.summary.additionalDetailsAria')}
                    >
                      {renderSummaryCards(normalizedSummary, 'tier2')}
                    </div>
                  )}
                </>
              )}

              {/* Contract Parties Subsection */}
              {partyCards.length > 0 && (
                <div id="section-parties" className="summary-parties">
                  <div className="summary-parties-header">
                    <h3>{t('documents:detail.parties.title')}</h3>
                    <p>{t('documents:detail.parties.subtitle')}</p>
                  </div>
                  <div className="parties-grid">
                    {partyCards}
                  </div>
                </div>
              )}
            </section>
          )}

        {/* Q&A section - only show when analysis is complete */}
        {!isProcessing && (
          <section id="section-qa" className="detail-section section-tone-5">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <SectionHeader
                  icon={<ChatIcon size={24} />}
                  title={t('documents:detail.questions.title')}
                  subtitle={t('documents:detail.questions.subtitle')}
                />
                <div style={{
                  padding: '0.5rem 0.75rem',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#047857',
                  minWidth: '140px',
                  textAlign: 'center',
                  fontWeight: 600
                }}>
                  {`${t('common:subscription.free')} • ${t('documents:detail.questions.history.count', { count: 1 })}`}
                </div>
              </div>
              <div className="qa-container-new">
                {suggestedQuestions.length > 0 && (
                  <div className="qa-suggested-questions">
                    {suggestedQuestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        className="qa-suggestion-chip"
                        onClick={() => {
                          const stripped = stripTemplateBraces(suggestion);
                          setQuestion(stripped.text);
                          setActiveTemplateVars(stripped.vars);
                          window.setTimeout(() => {
                            questionInputRef.current?.focus();
                            const first = stripped.ranges[0];
                            if (first) {
                              questionInputRef.current?.setSelectionRange(first.start, first.end);
                            } else {
                              questionInputRef.current?.setSelectionRange(stripped.text.length, stripped.text.length);
                            }
                          }, 0);
                        }}
                      >
                        {renderSuggestedQuestionLabel(suggestion)}
                      </button>
                    ))}
                  </div>
                )}
                <form onSubmit={handlePreviewQuestion} className="qa-form-new">
                  <textarea
                    ref={questionInputRef}
                    className="qa-input-new"
                    placeholder={t('documents:detail.questions.placeholder')}
                    value={question}
                    rows={1}
                    onChange={(event) => {
                      const next = event.target.value;
                      setQuestion(next);
                      if (activeTemplateVars.length > 0 && !shouldShowTemplateHint(next, activeTemplateVars)) {
                        setActiveTemplateVars([]);
                      }
                    }}
                    onInput={resizeQuestionInput}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                  />
                  <button
                    type="submit"
                    className="qa-submit-new"
                    disabled={!question.trim() || isPreviewAsking}
                  >
                    {isPreviewAsking ? <RefreshCwIcon size={18} className="animate-spin" /> : <ArrowRightIcon size={18} />}
                  </button>
                </form>
                {shouldShowTemplateHelp && (
                  <p className="qa-context-helper">
                    {activeTemplateVars.length === 1
                      ? t('documents:detail.questions.templateErrorSingle', { var: activeTemplateVars[0] })
                      : t('documents:detail.questions.templateErrorMultiple', { vars: activeTemplateVars.join(', ') })}
                  </p>
                )}
                <p className="qa-context-helper">
                  {t('documents:detail.questions.contextHelper')}
                </p>
              </div>
            </section>
        )}

        {/* Processing banner - shown at bottom when analysis is still in progress */}
        {isProcessing && (
          <section className="detail-section section-tone-1 processing-inline">
            <ContractProcessingCard
              title={t('documents:detail.processing.title')}
              stepTitle={processingStagesAnon[processingStage].title}
              stepIcon={processingStagesAnon[processingStage].icon}
              motivation={selectedMotivation}
            />
          </section>
        )}
      </div>

      {/* Footer */}
      <footer className="document-footer">
        <div className="document-footer-content">
          <div className="footer-left">
            <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo" />
            <div className="footer-text">
              <span className="footer-copyright">
                {t('common:footer.copyright', { year: new Date().getFullYear() })}
              </span>
              <span className="footer-tagline">
                {t('common:footer.tagline', { defaultValue: t('documents:preview.footerTagline') })}
              </span>
            </div>
          </div>
          <div className="footer-right">
            <span className="footer-tech">{t('common:footer.poweredBy')}</span>
          </div>
        </div>
      </footer>

        {showAuthModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">{authModalContent.title}</h2>
              <p className="modal-subtitle">{authModalContent.subtitle}</p>
              <div className="role-buttons">
                <button
                  className="role-button tenant-button signup-cta"
                  onClick={() => handleGoToAuth('/signup')}
                >
                  {t('common:nav.signup')}
                </button>
                <button
                  className="role-button landlord-button"
                  onClick={() => handleGoToAuth('/login')}
                >
                  {t('common:nav.login')}
                </button>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowAuthModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  fontWeight: 'normal'
                }}
              >
                {t('common:buttons.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Helper Components and Functions
const TruncatedText: React.FC<{ text: string; maxLength?: number }> = ({ text, maxLength = 100 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;

  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {isExpanded ? text : `${text.substring(0, maxLength)}...`}
      {' '}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-expand-btn"
        type="button"
      >
        {isExpanded ? tDocs('documents:detail.summary.collapseDetails') : tDocs('documents:detail.summary.expandDetails')}
      </button>
    </span>
  );
};

const formatValue = (value: any): string => {
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const notSpecified = tDocs('documents:detail.values.notSpecified');
  const yes = tDocs('documents:detail.values.yes');
  const no = tDocs('documents:detail.values.no');

  if (value === null || value === undefined) {
    return notSpecified;
  }

  const strValue = String(value).trim();

  const notFoundPatterns = [
    'N/A', 'n/a', 'NA',
    'não encontrado', 'não identificado', 'não especificado',
    'não consta', 'não informado', 'não disponível',
    'not found', 'not identified', 'not specified', 'not available'
  ];

  if (notFoundPatterns.some(pattern => strValue.toLowerCase() === pattern.toLowerCase())) {
    return notSpecified;
  }

  if (typeof value === 'boolean') {
    return value ? yes : no;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value)
      .filter(([, v]) => {
        const formatted = formatValue(v);
        return formatted && formatted !== notSpecified;
      })
      .map(([k, v]) => `${k}: ${formatValue(v)}`);
    return entries.join(', ') || notSpecified;
  }

  if (Array.isArray(value)) {
    const formatted = value
      .map(v => formatValue(v))
      .filter(v => v && v !== notSpecified);
    return formatted.join(', ') || notSpecified;
  }

  return strValue;
};

const getIconForField = (field: string): React.ReactNode => {
  const iconMap: { [key: string]: React.ReactNode } = {
    valor_aluguel: <DollarSignIcon size={20} />,
    prazo_locacao: <CalendarIcon size={20} />,
    data_inicio: <CalendarIcon size={20} />,
    data_termino: <CalendarIcon size={20} />,
    reajuste: <TrendingUpIcon size={20} />,
    garantia: <ShieldIcon size={20} />,
    fiador: <UserIcon size={20} />,
    multas: <AlertIcon size={20} />,
    pagamento: <CreditCardIcon size={20} />,
    condominio: <BuildingIcon size={20} />,
    animais_permitidos: <PawPrintIcon size={20} />,
    pintura: <PaintbrushIcon size={20} />,
    reparos: <WrenchIcon size={20} />,
    vistoria: <SearchIcon size={20} />,
    uso_imovel: <HomeIcon size={20} />,
    seguro: <ShieldIcon size={20} />,
    iptu: <BuildingIcon size={20} />,
    encargos_extras: <DollarSignIcon size={20} />,
    renovacao: <RefreshCwIcon size={20} />,
    notificacao: <MailIcon size={20} />,
  };
  return iconMap[field] || <ClipboardIcon size={20} />;
};

const formatFieldLabel = (key: string): string => {
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const fallback = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const translated = tDocs(`documents:detail.summary.fields.${key}`, { defaultValue: fallback });
  return translated || fallback;
};

const PARTY_META_KEYS = new Set(['existe', 'exists', 'present', 'is_present', 'isPresent']);

const isMeaningfulString = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return Boolean(normalized) && normalized !== 'não especificado' && normalized !== 'não aplica';
};

const hasMeaningfulValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return isMeaningfulString(value);
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') {
    return Object.entries(value).some(([key, inner]) => !PARTY_META_KEYS.has(key) && hasMeaningfulValue(inner));
  }
  return false;
};

const formatPartyFieldLabel = (key: string): string => {
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const labelMap: Record<string, string> = {
    nome: tDocs('documents:detail.parties.fields.name'),
    name: tDocs('documents:detail.parties.fields.name'),
    cpf: 'CPF',
    cpf_cnpj: 'CPF/CNPJ',
    cpfCnpj: 'CPF/CNPJ',
    cnpj: 'CNPJ',
    rg: 'RG',
    orgao_emissor: tDocs('documents:detail.parties.fields.issuingBody'),
    orgaoEmissor: tDocs('documents:detail.parties.fields.issuingBody'),
    nacionalidade: tDocs('documents:detail.parties.fields.nationality'),
    nationality: tDocs('documents:detail.parties.fields.nationality'),
    estado_civil: tDocs('documents:detail.parties.fields.maritalStatus'),
    marital_status: tDocs('documents:detail.parties.fields.maritalStatus'),
    maritalStatus: tDocs('documents:detail.parties.fields.maritalStatus'),
    profissao: tDocs('documents:detail.parties.fields.profession'),
    profession: tDocs('documents:detail.parties.fields.profession'),
    occupation: tDocs('documents:detail.parties.fields.profession'),
    tipo: tDocs('documents:detail.parties.fields.type'),
    type: tDocs('documents:detail.parties.fields.type'),
    creci: 'CRECI',
    responsavel: tDocs('documents:detail.parties.fields.responsible'),
    responsible: tDocs('documents:detail.parties.fields.responsible'),
    telefone: tDocs('documents:detail.parties.fields.phone'),
    phone: tDocs('documents:detail.parties.fields.phone'),
    email: 'Email',
    endereco: tDocs('documents:detail.parties.fields.address'),
    address: tDocs('documents:detail.parties.fields.address'),
    contact: tDocs('documents:detail.parties.fields.contact'),
    descricao: tDocs('documents:detail.parties.fields.description'),
    description: tDocs('documents:detail.parties.fields.description'),
    cidade: tDocs('documents:detail.parties.fields.city'),
    city: tDocs('documents:detail.parties.fields.city'),
    estado: 'UF',
    state: 'UF',
    cep: 'CEP',
    zip_code: 'CEP',
    zipCode: 'CEP',
  };
  return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const getPartyCardMeta = (key: string) => {
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const normalized = key.toLowerCase();

  if (['locatario', 'tenant'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.tenant'), icon: <UserIcon size={20} /> };
  }
  if (['locador', 'landlord'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.landlord'), icon: <HomeIcon size={20} /> };
  }
  if (['imobiliaria', 'agency', 'real_estate_agency', 'realestateagency'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.agency'), icon: <BuildingIcon size={20} /> };
  }
  if (['witnesses', 'testemunhas', 'witness'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.witness'), icon: <UserIcon size={20} /> };
  }
  if (['notary', 'cartorio', 'cartório'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.notary'), icon: <BuildingIcon size={20} /> };
  }
  if (['fiador', 'fiadores', 'guarantor', 'guarantors'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.guarantor'), icon: <ShieldIcon size={20} /> };
  }
  if (['imovel', 'property'].includes(normalized)) {
    return { title: tDocs('documents:detail.parties.property'), icon: <BuildingIcon size={20} /> };
  }

  return { title: formatPartyFieldLabel(key), icon: <UserIcon size={20} /> };
};

const buildPartyCards = (parties: any): React.ReactNode[] => {
  if (!parties || typeof parties !== 'object') return [];

  const cards: React.ReactNode[] = [];
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const notSpecified = tDocs('documents:detail.values.notSpecified');
  const nameKeys = new Set([
    'nome',
    'name',
    'nome_completo',
    'nome completo',
    'full_name',
    'fullname',
    'razao_social',
    'razão_social',
    'razao social',
    'razão social',
  ]);

  const PartyCard: React.FC<{
    cardKey: string;
    title: string;
    icon: React.ReactNode;
    name: string;
    details: ReadonlyArray<readonly [string, string]>;
  }> = ({ cardKey, title, icon, name, details }) => {
    const [expanded, setExpanded] = React.useState(false);
    const detailsId = `${cardKey}-details`;

    return (
      <div className="party-card">
        <div className="party-card-header">
          <span className="party-avatar">{icon}</span>
          <h3>{title}</h3>
        </div>
        <div className="party-card-body">
          <div className="party-name">{name}</div>

          {details.length > 0 && (
            <>
              <button
                type="button"
                className="summary-expand-btn party-expand-btn"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={detailsId}
              >
                <span>{expanded ? tDocs('documents:detail.parties.hideDetails') : tDocs('documents:detail.parties.showDetails')}</span>
                {expanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
              </button>

              {expanded && (
                <div id={detailsId} className="party-details" role="region" aria-label={`${tDocs('documents:detail.parties.showDetails')} - ${title}`}>
                  {details.map(([fieldKey, value]) => (
                    <div key={`${cardKey}-${fieldKey}`} className="party-field">
                      <span className="party-label">{formatPartyFieldLabel(fieldKey)}</span>
                      <span className="party-value">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const addPartyCard = (partyKey: string, partyValue: any, index?: number, customTitle?: string) => {
    if (!partyValue || typeof partyValue !== 'object') return;

    if (!hasMeaningfulValue(partyValue)) return;

    const meta = getPartyCardMeta(partyKey);
    const preferredOrder = [
      'nome',
      'name',
      'cpf_cnpj',
      'cpfCnpj',
      'cpf',
      'cnpj',
      'creci',
      'tipo',
      'type',
      'endereco',
      'address',
      'contact',
      'telefone',
      'phone',
      'email',
      'responsavel',
      'profissao',
      'occupation',
      'estado_civil',
      'marital_status',
      'descricao',
      'description',
    ];

    const entries = Object.entries(partyValue)
      .filter(([k]) => !PARTY_META_KEYS.has(k))
      .map(([k, v]) => [k, formatValue(v)] as const)
      .filter(([, v]) => v && v !== notSpecified)
      .sort(([a], [b]) => {
        const ai = preferredOrder.indexOf(a);
        const bi = preferredOrder.indexOf(b);
        const aRank = ai === -1 ? 999 : ai;
        const bRank = bi === -1 ? 999 : bi;
        if (aRank !== bRank) return aRank - bRank;
        return a.localeCompare(b);
      });

    if (entries.length === 0) return;

    const key = index === undefined ? partyKey : `${partyKey}-${index}`;
    const title = customTitle || (index === undefined ? meta.title : `${meta.title} ${index + 1}`);
    const nameEntry = entries.find(([fieldKey]) => nameKeys.has(String(fieldKey).toLowerCase()));
    const name = nameEntry?.[1] || '';
    const details = entries.filter(([fieldKey]) => fieldKey !== nameEntry?.[0]);

    cards.push(
      <PartyCard
        key={key}
        cardKey={key}
        title={title}
        icon={meta.icon}
        name={name}
        details={details}
      />
    );
  };

  const arePartiesSimilar = (party1: any, party2: any): boolean => {
    if (!party1 || !party2) return false;
    if (typeof party1 !== 'object' || typeof party2 !== 'object') return false;

    const getFields = (party: any) => {
      return Object.entries(party)
        .filter(([k]) => !PARTY_META_KEYS.has(k))
        .filter(([, v]) => {
          const formatted = formatValue(v);
          return formatted && formatted !== notSpecified;
        })
        .reduce((acc, [k, v]) => {
          acc[k.toLowerCase()] = String(formatValue(v)).toLowerCase().trim();
          return acc;
        }, {} as Record<string, string>);
    };

    const fields1 = getFields(party1);
    const fields2 = getFields(party2);

    if (Object.keys(fields1).length === 0 || Object.keys(fields2).length === 0) {
      return false;
    }

    const nameKeys = ['nome', 'name'];
    const idKeys = ['cpf', 'cnpj', 'cpf_cnpj', 'cpfcnpj'];

    const getName = (fields: Record<string, string>) => {
      for (const key of nameKeys) {
        if (fields[key]) return fields[key];
      }
      return null;
    };

    const getId = (fields: Record<string, string>) => {
      for (const key of idKeys) {
        if (fields[key]) return fields[key];
      }
      return null;
    };

    const name1 = getName(fields1);
    const name2 = getName(fields2);
    const id1 = getId(fields1);
    const id2 = getId(fields2);

    if (name1 && name2 && name1 === name2) return true;

    if (id1 && id2 && id1 === id2) return true;

    return false;
  };

  const recognizedKeys = [
    'locador',
    'locatario',
    'fiadores',
    'fiador',
    'imobiliaria',
    'imovel',
    'landlord',
    'tenant',
    'guarantor',
    'guarantors',
    'agency',
    'property',
  ];

  const hasRecognized = recognizedKeys.some((k) => parties[k] !== undefined);
  if (hasRecognized) {
    const processedKeys = new Set<string>();

    const add = (k: string, v: any) => {
      if (processedKeys.has(k)) return;

      if (['landlord', 'locador'].includes(k)) {
        const agencyKey = parties['agency'] !== undefined ? 'agency' :
                         parties['imobiliaria'] !== undefined ? 'imobiliaria' : null;

        if (agencyKey && arePartiesSimilar(v, parties[agencyKey])) {
          const landlordMeta = getPartyCardMeta(k);
          const agencyMeta = getPartyCardMeta(agencyKey);
          const combinedTitle = `${agencyMeta.title} / ${landlordMeta.title}`;

          if (Array.isArray(v)) {
            v.forEach((item, idx) => addPartyCard(k, item, idx, `${combinedTitle} ${idx + 1}`));
          } else {
            addPartyCard(k, v, undefined, combinedTitle);
          }

          processedKeys.add(k);
          processedKeys.add(agencyKey);
          return;
        }
      }

      if (['agency', 'imobiliaria'].includes(k) &&
          (processedKeys.has('landlord') || processedKeys.has('locador'))) {
        return;
      }

      if (Array.isArray(v)) {
        v.forEach((item, idx) => addPartyCard(k, item, idx));
      } else {
        addPartyCard(k, v);
      }
      processedKeys.add(k);
    };

    recognizedKeys.forEach((k) => {
      const v = (parties as any)[k];
      if (v === undefined) return;
      add(k, v);
    });

    return cards;
  }

  Object.entries(parties).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      v.forEach((item, idx) => addPartyCard(k, item, idx));
      return;
    }
    if (typeof v === 'object') addPartyCard(k, v);
  });

  return cards;
};

const renderSummaryCards = (summary: any, tier: 'tier1' | 'tier2' | 'all' = 'all'): React.ReactNode[] => {
  const cards: React.ReactNode[] = [];
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const notSpecified = tDocs('documents:detail.values.notSpecified');

  const buildDisplaySummary = (data: any) => {
    const enriched = { ...(data || {}) };
    const hasStart = enriched.data_inicio && enriched.data_inicio !== notSpecified;
    const hasEnd = enriched.data_termino && enriched.data_termino !== notSpecified;
    const hasPrazo = enriched.prazo_locacao && enriched.prazo_locacao !== notSpecified;
    if (hasPrazo || hasStart || hasEnd) {
      const prazoObj: Record<string, any> = {};
      if (typeof enriched.prazo_locacao === 'object' && enriched.prazo_locacao !== null) {
        Object.assign(prazoObj, enriched.prazo_locacao);
      } else if (hasPrazo) {
        prazoObj.prazo = enriched.prazo_locacao;
      }
      if (hasStart && !prazoObj.data_inicio) prazoObj.data_inicio = enriched.data_inicio;
      if (hasEnd && !prazoObj.data_termino) prazoObj.data_termino = enriched.data_termino;
      enriched.prazo_locacao = prazoObj;
    }
    delete enriched.data_inicio;
    delete enriched.data_termino;
    return enriched;
  };

  const displaySummary = buildDisplaySummary(summary);

  const tier1Fields = [
    'valor_aluguel',
    'prazo_locacao',
    'multas',
    'reajuste',
  ];

  const tier2Fields = [
    'pagamento',
    'condominio',
    'reparos',
    'iptu',
    'seguro',
    'vistoria',
    'pintura',
    'uso_imovel',
    'renovacao',
    'notificacao',
    'encargos_extras',
    'garantia',
    'animais_permitidos',
  ];

  const renderCard = (key: string, value: any) => {
    if (!value || value === notSpecified) return;

    const isObjectValue = typeof value === 'object' && !Array.isArray(value);
    const hasValidChildren =
      isObjectValue &&
      Object.values(value).some((child) => child && child !== notSpecified);

    if (!isObjectValue) {
      cards.push(
        <SummaryCard
          key={key}
          icon={getIconForField(key)}
          label={formatFieldLabel(key)}
          value={formatValue(value)}
        />
      );
      return;
    }

    if (hasValidChildren) {
      const displayValue = (
        <div className="summary-card-details">
          {Object.entries(value)
            .filter(([, v]) => v && v !== notSpecified)
            .map(([childKey, childVal]) => (
              <div key={childKey} className="summary-detail-item">
                <span className="summary-detail-label">{formatFieldLabel(childKey)}</span>
                <span className="summary-detail-value">
                  <TruncatedText text={formatValue(childVal)} maxLength={100} />
                </span>
              </div>
            ))}
        </div>
      );

      cards.push(
        <SummaryCard
          key={key}
          icon={getIconForField(key)}
          label={formatFieldLabel(key)}
          value={displayValue}
        />
      );
    }
  };

  if (tier === 'tier1' || tier === 'all') {
    tier1Fields.forEach((field) => renderCard(field, displaySummary[field]));
  }

  // Fields consolidated into prazo_locacao - don't show separately
  const consolidatedFields = ['data_inicio', 'data_termino'];

  if (tier === 'tier2' || tier === 'all') {
    tier2Fields.forEach((field) => renderCard(field, displaySummary[field]));

    Object.entries(displaySummary).forEach(([key, value]) => {
      if (!tier1Fields.includes(key) && !tier2Fields.includes(key) && !consolidatedFields.includes(key)) {
        renderCard(key, value);
      }
    });
  }

  return cards;
};

export default AnonymousAnalysis;

const RiskPreviewCard: React.FC<{
  risk: any;
  index: number;
  locked: boolean;
  onUnlock: () => void;
}> = ({ risk, index, locked, onUnlock }) => {
  const { t } = useTranslation(['documents', 'common']);

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'high':
        return { label: t('documents:detail.risks.card.severity.high') };
      case 'medium':
        return { label: t('documents:detail.risks.card.severity.medium') };
      default:
        return { label: t('documents:detail.risks.card.severity.low') };
    }
  };

  const config = getSeverityConfig(risk.severity);
  const severityClass = risk.severity === 'high' ? 'high' : risk.severity === 'medium' ? 'medium' : 'low';
  const financialImpact = typeof risk.financial_impact_brl === 'number' && risk.financial_impact_brl > 0
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(risk.financial_impact_brl)
    : null;

  const normalizedTitle = (value?: string) => value?.replace(/\s+/g, ' ').trim() || '';

  const getProblemTitle = () => {
    const title = normalizedTitle(risk.title);
    return title
      .replace(/^(você pode|voce pode|vale|recomenda-se|sugerimos|é importante|é recomendável)\s+/i, '')
      .replace(/^(negociar|solicitar|pedir|verificar|confirmar|revisar|ajustar)\s+/i, '')
      .trim() || title;
  };

  const displayTitle = getProblemTitle();

  const getImpactSummary = () => {
    const impact = normalizedTitle(risk.potential_impact || '');
    const explanation = normalizedTitle(risk.explanation || '');
    const titleLower = displayTitle.toLowerCase();
    const impactLower = impact.toLowerCase();

    if (impactLower && !impactLower.includes(titleLower)) {
      return impact;
    }

    if (explanation && !explanation.toLowerCase().includes(titleLower)) {
      const match = /^[^.!?]+[.!?]/.exec(explanation);
      return match ? match[0].trim() : `${explanation.substring(0, 80)}...`;
    }

    return impact || t('documents:detail.risks.overview.description.medium', { defaultValue: 'May create unexpected costs or obligations.' });
  };

  const impactSummary = getImpactSummary();
  const recommendationText = normalizedTitle(risk.recommendation?.suggestion || '');
  const actionItems = Array.isArray(risk.action_items)
    ? risk.action_items.filter((item: any) => typeof item === 'string' && item.trim())
    : recommendationText
      ? [recommendationText]
      : [t('documents:detail.risks.locked.description')];
  const hasLegalZone = Boolean(risk.clause_text || risk.explanation || risk.legal_basis || risk.legal_reference || risk.suggested_text);
  const hasScenarios = Array.isArray(risk.when_it_happens) && risk.when_it_happens.length > 0;

  if (locked) {
    return (
      <div
        id={`risk-${index}`}
        className={`risk-tab risk-tab--${severityClass} has-locked-overlay`}
      >
        <div
          className="locked-content-overlay"
          role="button"
          tabIndex={0}
          aria-label={t('documents:detail.risks.locked.upgradeAriaLabel')}
          onClick={onUnlock}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onUnlock();
            }
          }}
        >
          <div className="locked-content-message">
            <ZapIcon size={48} />
            <h4>{t('documents:detail.risks.locked.title')}</h4>
            <p>{t('documents:detail.risks.locked.description')}</p>
          </div>
        </div>
        <div className="risk-tab-inner">
          <div className="risk-tab-head">
            <h3 className="risk-tab-title">{displayTitle || t('documents:detail.risks.card.counter', { current: index + 1, total: index + 1 })}</h3>
          </div>
          <div className="risk-tab-severity-line">
            <span className={`risk-sev-dot risk-sev-dot--${severityClass}`}>●</span>
            <span className={`risk-sev-label risk-sev-${severityClass}`}>{config.label}</span>
            <span className="risk-sev-sep">·</span>
            <span className="risk-sev-neg">
              {t('documents:detail.risks.card.negotiationLevel')} {t('documents:detail.risks.card.negotiationLevels.alta')}
            </span>
          </div>
          <p className="risk-tab-impact">{impactSummary}</p>
          <div className="risk-tab-actions">
            <p className="risk-tab-actions-heading">{t('documents:detail.risks.card.actionItems')}</p>
            <ul className="risk-tab-actions-list">
              {actionItems.slice(0, 3).map((item: string, itemIndex: number) => (
                <li key={itemIndex} className="risk-action-row">
                  <span className="risk-action-text">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`risk-${index}`}
      className={`risk-tab risk-tab--${severityClass}`}
    >
      <div className="risk-tab-inner">
        <div className="risk-tab-head">
          <h3 className="risk-tab-title">
            {displayTitle || t('documents:detail.risks.card.counter', { current: index + 1, total: index + 1 })}
          </h3>
          <button type="button" className="risk-tab-understood" onClick={onUnlock}>
            <CheckIcon size={14} />
            <span>{t('documents:detail.risks.card.understood')}</span>
          </button>
        </div>

        <div className="risk-tab-severity-line">
          <span className={`risk-sev-dot risk-sev-dot--${severityClass}`}>●</span>
          <span className="risk-sev-label">{config.label}</span>
          {financialImpact && (
            <>
            <span className="risk-sev-sep">·</span>
            <span className="risk-sev-financial">
              {t('documents:detail.risks.card.financialImpactTag', { amount: financialImpact })}
            </span>
            </>
          )}
          <span className="risk-sev-sep">·</span>
          <span className="risk-sev-neg">
            {t('documents:detail.risks.card.negotiationLevel')} {t('documents:detail.risks.card.negotiationLevels.alta')}
          </span>
        </div>

        <p className="risk-tab-impact">{impactSummary}</p>

        <div className="risk-tab-actions">
          <p className="risk-tab-actions-heading">{t('documents:detail.risks.card.actionItems')}</p>
          <ul className="risk-tab-actions-list">
            {actionItems.slice(0, 3).map((item: string, itemIndex: number) => (
              <li key={itemIndex} className="risk-action-row">
                <span className="risk-action-text">{item}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="risk-add-all-btn"
            onClick={onUnlock}
          >
            {t('documents:detail.risks.card.addToNegotiation')}
          </button>
        </div>

        <div className="risk-deep-promo">
          <p className="risk-deep-promo-teaser">
            {t('documents:detail.risks.card.deepTeaser')}
          </p>
          <button
            type="button"
            className="risk-deep-promo-btn"
            onClick={onUnlock}
          >
            {t('documents:detail.risks.card.deepOpen')}
          </button>
        </div>

        {hasLegalZone && (
          <details className="risk-fundamentos">
            <summary className="risk-fundamentos-summary">
              <span>{t('documents:detail.risks.card.legalFundamentos')}</span>
              <span className="risk-fundamentos-meta">
                {t('documents:detail.risks.card.clauseCount')} · {t('documents:detail.risks.card.legalBasisMeta')}
              </span>
              <ChevronDownIcon size={13} className="risk-fundamentos-chevron" />
            </summary>
            <div className="risk-fundamentos-body">
              {risk.clause_text && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{t('documents:detail.risks.card.contractClause')}</p>
                  <div className="risk-tab-clause">"{risk.clause_text}"</div>
                </div>
              )}
              {risk.explanation && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{t('documents:detail.risks.card.whyItMatters')}</p>
                  <p className="risk-tab-detail-text">{risk.explanation}</p>
                </div>
              )}
              {risk.legal_basis && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{t('documents:detail.risks.card.legalBasis')}</p>
                  <p className="risk-tab-detail-text">{risk.legal_basis}</p>
                </div>
              )}
            </div>
          </details>
        )}

        {(hasScenarios || !locked) && (
          <div className="risk-tab-footer">
            <button type="button" className="risk-tab-show-details" onClick={onUnlock}>
              <span>{t('documents:detail.risks.card.showScenarios')}</span>
              <ChevronDownIcon size={13} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};
